import {
  forwardRef,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { robotsApi } from '@/api/robots';
import { Button, Input } from '@/components/Form';
import { Icon, type IconName } from '@/components/Icon';
import gen1Img from '@/assets/robots/gen1.webp';
import gen2Img from '@/assets/robots/gen2.webp';
import zephyrImg from '@/assets/robots/zephyr.webp';

// 로봇 상세 '부품' 탭의 끌어 놓기 보드 공용 조각(2026-10-28 오너).
//   · PickList    — 왼쪽 목록. 누르면 더하고 다시 누르면 빼기(Ctrl 없이) · 빈 곳에서 끌어 선택 상자,
//                   끌어서 로봇에 놓기.
//   · RobotStage  — 가운데 로봇 그림. 끌면 떠오르고, 올리면 빛나고, 놓으면 이름표가 빨려 들어간다.
//   · FoldSection — 위 두 개를 담는 접는 구획. 아래 막대(오너 그림: 가운데 꺾쇠가 있는 초록 막대)로 여닫는다.
// 부품 장착(PartInstallBoard)과 적용 변경점(EcnApplyBoard)이 같은 조각을 쓴다.
//
// 움직임은 자주 하는 동작일수록 작게 한다 — 고르기는 살짝, 놓기 · 성공은 눈에 띄게.
// 모션 줄이기 설정이면 위치 · 크기 변화는 빼고 색만 남긴다(globals.css 의 pib-* 와 prefersReducedMotion).

// 선택 상자가 이만큼은 움직여야 '끌기'로 본다 — 그보다 짧으면 빈 곳 클릭(선택 해제).
const MARQUEE_THRESHOLD = 4;

// 강한 ease-out / 화면 안 이동용 ease-in-out — tokens.css 의 --ease-out-strong · --ease-in-out-strong 과 같은 값.
// WAAPI 는 CSS 변수를 못 읽으므로 여기 한 번 더 적는다.
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';
const EASE_IN_OUT = 'cubic-bezier(0.77, 0, 0.175, 1)';

/**
 * 모델 코드 → 로봇 그림(오너가 준 그림의 누끼). GEN1_D1 같은 세부 코드도 앞머리로 가른다.
 * 그림이 없는 모델(HEADLESS 등)은 GEN2 그림으로 대신한다.
 */
export function robotImageFor(modelCode: string | null | undefined): { src: string; kind: string } {
  const code = (modelCode ?? '').toUpperCase();
  if (code.startsWith('GEN1')) return { src: gen1Img, kind: 'GEN1' };
  if (code.startsWith('ZEPHYR')) return { src: zephyrImg, kind: 'ZEPHYR' };
  return { src: gen2Img, kind: 'GEN2' };
}

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

// ── 접는 구획 ──

export function FoldSection({
  title,
  summary,
  defaultOpen,
  testId,
  children,
}: {
  title: string;
  /** 닫혀 있어도 보이는 한 줄(예: "장착된 부품 4"). */
  summary?: ReactNode;
  defaultOpen: boolean;
  testId: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();
  const label = `${title} ${open ? '접기' : '펼치기'}`;
  return (
    <section data-testid={testId} data-open={open}>
      <button
        type="button"
        className="pib-fold-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={bodyId}
        data-testid={`${testId}-head`}
      >
        <span className="pib-fold-title">{title}</span>
        {summary != null && <span className="pib-fold-summary">{summary}</span>}
      </button>
      {/* 닫혀 있는 동안은 inert — 안 보이는 칸에 Tab 이 들어가지 않게. 내용은 그대로 두어
          다시 열 때 고른 것 · 찾기 글자가 남는다. */}
      <div
        id={bodyId}
        className="pib-fold-body"
        data-open={open}
        {...{ inert: open ? undefined : '' }}
      >
        <div style={{ minHeight: 0, overflow: 'hidden' }}>
          <div className="pib-fold-inner">{children}</div>
        </div>
      </div>
      <button
        type="button"
        className="pib-fold-bar"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={bodyId}
        aria-label={label}
        title={label}
        data-testid={`${testId}-bar`}
      >
        <span className="pib-fold-track">
          <span className="pib-fold-chev">
            <Icon name="chev-down" size={16} strokeWidth={3} aria-hidden />
          </span>
        </span>
      </button>
    </section>
  );
}

// ── 가운데: 로봇 무대 ──

export interface RobotStageHandle {
  /** 성공 — 로봇 둘레로 불꽃이 퍼진다. */
  burst: () => void;
}

export const RobotStage = forwardRef<
  RobotStageHandle,
  {
    robotId: string;
    dragType: string;
    canDrop: boolean;
    /** 같은 종류를 끄는 중인가(PickList 가 알린다) — 로봇이 떠올라 기다린다. */
    dragging: boolean;
    hints: { idle: string; ready: string; over: string; denied: string };
    /** 놓은 id 를 이름으로 — 로봇으로 빨려 드는 이름표에 쓴다. */
    nameOf: (id: string) => string | undefined;
    onDropIds: (ids: string[]) => void;
    testId: string;
  }
>(function RobotStage(
  { robotId, dragType, canDrop, dragging, hints, nameOf, onDropIds, testId },
  ref,
) {
  const [over, setOver] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const robotRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    burst: () => burstSparks(stageRef.current, robotRef.current),
  }));

  // 로봇 상세와 같은 queryKey — 탭을 열어 둔 상태에선 추가 네트워크가 없다.
  const robotQ = useQuery({
    queryKey: ['robot', robotId],
    queryFn: () => robotsApi.get(robotId),
    staleTime: 60_000,
  });
  const image = robotImageFor(robotQ.data?.model?.code);
  const serial = robotQ.data?.serialShort ?? robotQ.data?.serialLong;

  function onDragOver(e: DragEvent<HTMLDivElement>): void {
    if (!canDrop || !Array.from(e.dataTransfer.types).includes(dragType)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!over) setOver(true);
  }

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    const raw = e.dataTransfer.getData(dragType);
    if (!raw) return;
    e.preventDefault();
    setOver(false);
    let ids: string[];
    try {
      ids = JSON.parse(raw) as string[];
    } catch {
      return; // 다른 데서 끌어온 값 — 무시.
    }
    const names = ids.map(nameOf).filter((n): n is string => !!n);
    flyIntoRobot(names, e.clientX, e.clientY, robotRef.current);
    onDropIds(ids);
  }

  const state = over ? 'over' : dragging ? 'ready' : 'idle';
  return (
    <div
      ref={stageRef}
      className="pib-stage"
      data-state={state}
      onDragOver={onDragOver}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
      }}
      onDrop={onDrop}
      data-testid={testId}
      aria-label={`로봇 그림 — ${hints.idle}`}
      style={stageStyle}
    >
      <div aria-hidden className="pib-spot" />
      {/* 발밑 그림자와 고리는 로봇 그림 바로 아래에 붙는다. */}
      <div style={{ position: 'relative', maxWidth: '100%' }}>
        <div aria-hidden className="pib-floor" />
        <div aria-hidden className="pib-ring" />
        <div ref={robotRef} className="pib-robot-bounce">
          <img
            src={image.src}
            alt={`${image.kind} 로봇`}
            className="pib-robot"
            draggable={false}
            data-testid={`${testId}-figure`}
            data-kind={image.kind}
            style={{
              display: 'block',
              maxWidth: '100%',
              maxHeight: image.kind === 'ZEPHYR' ? 200 : 300,
              userSelect: 'none',
            }}
          />
        </div>
      </div>
      <div style={nameplateStyle}>
        {serial && <span style={{ fontFamily: 'var(--font-mono)' }}>#{serial}</span>}
        <span style={{ color: 'var(--fg-muted)' }}>
          {robotQ.data?.model?.displayName ?? image.kind}
        </span>
      </div>
      <div className="pib-hint" style={{ fontSize: 12 }}>
        {!canDrop
          ? hints.denied
          : state === 'over'
            ? hints.over
            : state === 'ready'
              ? hints.ready
              : hints.idle}
      </div>
    </div>
  );
});

// ── 놓기 · 성공 효과(WAAPI) — 한 번 쓰고 지우는 요소라 React 상태로 두지 않는다. ──

/** 놓은 자리에서 이름표가 로봇 가운데로 빨려 들어가고, 로봇이 한 번 출렁인다. */
function flyIntoRobot(
  names: string[],
  fromX: number,
  fromY: number,
  robot: HTMLElement | null,
): void {
  if (!robot || typeof robot.animate !== 'function' || prefersReducedMotion()) return;
  const r = robot.getBoundingClientRect();
  const toX = r.left + r.width / 2;
  const toY = r.top + r.height * 0.45;
  const shown = names.slice(0, 6);
  shown.forEach((name, i) => {
    const el = document.createElement('div');
    el.className = 'pib-flyer';
    el.textContent = name;
    el.style.left = `${fromX}px`;
    el.style.top = `${fromY}px`;
    document.body.appendChild(el);
    // 여러 개면 살짝 흩어진 자리에서 출발해 하나씩 들어간다.
    const sx = (i - (shown.length - 1) / 2) * 14;
    const sy = -i * 6;
    el.animate(
      [
        { transform: `translate(-50%, -50%) translate(${sx}px, ${sy}px) scale(1)`, opacity: 1 },
        {
          transform: `translate(-50%, -50%) translate(${toX - fromX}px, ${toY - fromY}px) scale(0.4)`,
          opacity: 0,
        },
      ],
      { duration: 420, delay: i * 50, easing: EASE_IN_OUT, fill: 'both' },
    ).onfinish = () => el.remove();
  });
  robot.animate(
    [
      { transform: 'scale(1)' },
      { transform: 'scale(0.96, 1.03)', offset: 0.35 },
      { transform: 'scale(1.03, 0.98)', offset: 0.65 },
      { transform: 'scale(1)' },
    ],
    { duration: 420, delay: 300 + (shown.length - 1) * 50, easing: EASE_OUT },
  );
}

const SPARK_COLORS = ['var(--brand)', '#f5b301', '#3b82f6', '#ef6c4a'];

/** 성공 — 로봇 둘레로 작은 불꽃이 퍼졌다가 사라진다. */
export function burstSparks(stage: HTMLElement | null, robot: HTMLElement | null): void {
  if (!stage || !robot || typeof robot.animate !== 'function' || prefersReducedMotion()) return;
  const s = stage.getBoundingClientRect();
  const r = robot.getBoundingClientRect();
  const cx = r.left - s.left + r.width / 2;
  const cy = r.top - s.top + r.height * 0.4;
  const count = 14;
  for (let i = 0; i < count; i += 1) {
    const el = document.createElement('div');
    el.className = 'pib-spark';
    el.style.left = `${cx}px`;
    el.style.top = `${cy}px`;
    el.style.background = SPARK_COLORS[i % SPARK_COLORS.length];
    stage.appendChild(el);
    const angle = (i / count) * Math.PI * 2 + (i % 2) * 0.2;
    const dist = 70 + (i % 3) * 22;
    const x = Math.cos(angle) * dist;
    const y = Math.sin(angle) * dist * 0.8;
    el.animate(
      [
        { transform: 'translate(-50%, -50%) scale(0.6)', opacity: 1 },
        {
          transform: `translate(-50%, -50%) translate(${x}px, ${y}px) scale(1)`,
          opacity: 1,
          offset: 0.6,
        },
        {
          transform: `translate(-50%, -50%) translate(${x * 1.15}px, ${y * 1.15 + 12}px) scale(0.7)`,
          opacity: 0,
        },
      ],
      { duration: 700, delay: (i % 4) * 25, easing: EASE_OUT, fill: 'both' },
    ).onfinish = () => el.remove();
  }
}

// ── 왼쪽: 고르고 끄는 목록 ──

export interface PickItem {
  id: string;
  title: string;
  meta: string;
  /** 찾기 칸이 보는 글(제목 · 메타 외에 더 찾을 말). */
  searchText: string;
  /** 이 로봇에 이미 붙어 있으면 그 표시(예: "장착됨 · GAS_VALVE"). */
  badge?: string | null;
  /** 마우스를 올리면 뜨는 설명. */
  hint?: string;
}

export function PickList({
  title,
  items,
  loading,
  error,
  emptyText,
  searchPlaceholder,
  dragType,
  icon,
  actionVerb,
  badgeLabel,
  testIdPrefix,
  toolbar,
  footer,
  onDragState,
  onAction,
}: {
  title: string;
  items: PickItem[];
  loading: boolean;
  error: string | null;
  emptyText: string;
  searchPlaceholder: string;
  dragType: string;
  icon: IconName;
  /** 고른 것을 한꺼번에 처리하는 단추의 말(장착 · 적용). */
  actionVerb: string;
  /** 머리에 보이는 '이미 붙은 것' 개수의 말(장착됨 · 적용됨). */
  badgeLabel: string;
  testIdPrefix: string;
  toolbar?: ReactNode;
  footer?: ReactNode;
  onDragState: (dragging: boolean) => void;
  onAction: (ids: string[]) => void;
}) {
  const p = testIdPrefix;
  const [keyword, setKeyword] = useState('');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  // 지금 손에 든(끌고 있는) 것 — 목록에서 흐리게 보여 '들려 나갔다'는 느낌을 준다.
  const [held, setHeld] = useState<Set<string>>(() => new Set());
  const [marquee, setMarquee] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const marqueeRef = useRef<{ x0: number; y0: number; base: Set<string>; moved: boolean } | null>(
    null,
  );

  const visible = useMemo(() => {
    const k = keyword.trim().toLowerCase();
    if (!k) return items;
    return items.filter((it) =>
      `${it.title} ${it.meta} ${it.searchText}`.toLowerCase().includes(k),
    );
  }, [items, keyword]);
  const badgeCount = visible.filter((it) => it.badge).length;

  // 찾기로 가려진 것이 선택에 남아 같이 끌려가지 않게, 늘 보이는 것만 센다.
  const selectedIds = useMemo(
    () => visible.filter((it) => selected.has(it.id)).map((it) => it.id),
    [visible, selected],
  );

  function contentPoint(clientX: number, clientY: number): { x: number; y: number } {
    const el = listRef.current!;
    const r = el.getBoundingClientRect();
    return { x: clientX - r.left + el.scrollLeft, y: clientY - r.top + el.scrollTop };
  }

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>): void {
    const el = listRef.current;
    if (!el || e.button !== 0) return;
    if ((e.target as HTMLElement).closest('[data-pick-chip]')) return;
    // 스크롤 막대를 잡은 경우는 선택 상자를 만들지 않는다.
    const r = el.getBoundingClientRect();
    if (el.clientWidth > 0 && e.clientX - r.left > el.clientWidth) return;
    const pt = contentPoint(e.clientX, e.clientY);
    marqueeRef.current = {
      x0: pt.x,
      y0: pt.y,
      // 2026-11-04 오너 "CTRL 클릭 안하고도 여러개 클릭 되게" — 선택 상자는 이미 고른 것에 더한다.
      base: new Set(selectedIds),
      moved: false,
    };
    el.setPointerCapture?.(e.pointerId);
  }

  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>): void {
    const m = marqueeRef.current;
    const el = listRef.current;
    if (!m || !el) return;
    // 목록 위아래로 벗어나면 그쪽으로 굴려 가며 이어서 고른다.
    const r = el.getBoundingClientRect();
    if (e.clientY < r.top) el.scrollTop -= 12;
    else if (e.clientY > r.bottom) el.scrollTop += 12;

    const pt = contentPoint(e.clientX, e.clientY);
    if (
      !m.moved &&
      Math.abs(pt.x - m.x0) < MARQUEE_THRESHOLD &&
      Math.abs(pt.y - m.y0) < MARQUEE_THRESHOLD
    ) {
      return;
    }
    m.moved = true;
    const box = {
      left: Math.min(m.x0, pt.x),
      top: Math.min(m.y0, pt.y),
      width: Math.abs(pt.x - m.x0),
      height: Math.abs(pt.y - m.y0),
    };
    setMarquee(box);

    const next = new Set(m.base);
    el.querySelectorAll<HTMLElement>('[data-pick-chip]').forEach((chip) => {
      const c = chip.getBoundingClientRect();
      const left = c.left - r.left + el.scrollLeft;
      const top = c.top - r.top + el.scrollTop;
      if (
        left < box.left + box.width &&
        left + c.width > box.left &&
        top < box.top + box.height &&
        top + c.height > box.top
      ) {
        next.add(chip.dataset.pickChip!);
      }
    });
    setSelected(next);
  }

  function onPointerUp(e: ReactPointerEvent<HTMLDivElement>): void {
    const m = marqueeRef.current;
    if (!m) return;
    marqueeRef.current = null;
    setMarquee(null);
    listRef.current?.releasePointerCapture?.(e.pointerId);
    // 빈 곳을 그냥 누르면 선택을 모두 푼다.
    if (!m.moved) setSelected(new Set());
  }

  function act(ids: string[]): void {
    onAction(ids);
    setSelected(new Set());
  }

  // 누르면 더하고 다시 누르면 뺀다(Ctrl 없이 여러 개 — 로봇 배정 장착대와 같다).
  function onChipClick(id: string): void {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function onChipDragStart(e: DragEvent<HTMLButtonElement>, item: PickItem): void {
    // 고른 것을 잡으면 고른 것 전부, 안 고른 것을 잡으면 그것 하나만 끈다.
    const ids = selected.has(item.id) ? selectedIds : [item.id];
    if (!selected.has(item.id)) setSelected(new Set([item.id]));
    e.dataTransfer.setData(dragType, JSON.stringify(ids));
    e.dataTransfer.effectAllowed = 'copy';
    // 커서에 붙는 그림 — 한 장이면 이름, 여러 장이면 겹친 카드에 개수.
    const ghost = ghostRef.current;
    if (ghost && e.dataTransfer.setDragImage) {
      ghost.textContent = ids.length > 1 ? `${ids.length}개` : item.title;
      ghost.dataset.stack = String(Math.min(ids.length, 3));
      e.dataTransfer.setDragImage(ghost, 18, 18);
    }
    setHeld(new Set(ids));
    onDragState(true);
  }

  return (
    <div style={{ ...panelStyle, display: 'flex', flexDirection: 'column', padding: 12, gap: 8 }}>
      <div style={{ ...panelHeaderStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1 }}>
          {title} <span style={{ color: 'var(--fg-muted)' }}>({visible.length})</span>
          {badgeCount > 0 && (
            <span
              style={{ marginLeft: 6, fontSize: 11, fontWeight: 500, color: 'var(--fg-success)' }}
            >
              {badgeLabel} {badgeCount}
            </span>
          )}
        </span>
        {selectedIds.length > 0 && (
          <Button
            type="button"
            onClick={() => act(selectedIds)}
            data-testid={`${p}-install-selected`}
            style={{ height: 26, padding: '0 10px', fontSize: 12 }}
          >
            {selectedIds.length}개 {actionVerb}
          </Button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <Input
          placeholder={searchPlaceholder}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          aria-label={`${title} 찾기`}
          data-testid={`${p}-catalog-search`}
          style={{ flex: 1, minWidth: 0 }}
        />
        {toolbar}
      </div>
      {footer}
      <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
        누르면 고르고 다시 누르면 풀립니다. 빈 곳에서 끌면 여러 개를 한꺼번에 고릅니다.
      </div>

      <div
        ref={listRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        data-testid={`${p}-catalog-list`}
        role="listbox"
        aria-multiselectable
        aria-label={title}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          // 양옆과 맨 아래에 끌기 시작할 빈 곳을 남긴다(칸 위에서 끌면 그 칸을 옮기게 된다).
          padding: '2px 18px 48px',
          userSelect: 'none',
        }}
      >
        {loading ? (
          <div style={emptyStyle}>불러오는 중…</div>
        ) : error ? (
          <div style={{ ...emptyStyle, color: 'var(--fg-danger)' }}>{error}</div>
        ) : visible.length === 0 ? (
          <div style={emptyStyle}>{items.length === 0 ? emptyText : '찾는 것이 없습니다.'}</div>
        ) : (
          visible.map((it) => (
            <button
              key={it.id}
              type="button"
              role="option"
              aria-selected={selected.has(it.id)}
              className="pib-chip"
              data-held={held.has(it.id) || undefined}
              data-installed={it.badge ? true : undefined}
              data-pick-chip={it.id}
              data-testid={`${p}-chip-${it.id}`}
              draggable
              onClick={() => onChipClick(it.id)}
              onDoubleClick={() => act([it.id])}
              onDragStart={(e) => onChipDragStart(e, it)}
              onDragEnd={(e) => {
                setHeld(new Set());
                onDragState(false);
                // 로봇에 놓았으면 고른 것을 비운다 — 다시 끌 때 방금 놓은 것이 딸려 가지 않게.
                if (e.dataTransfer.dropEffect !== 'none') setSelected(new Set());
              }}
              title={it.hint}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  minWidth: 0,
                  maxWidth: '100%',
                }}
              >
                <Icon name={icon} size={14} aria-hidden style={{ flexShrink: 0 }} />
                <span
                  style={{
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {it.title}
                </span>
              </span>
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--fg-muted)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}
              >
                {it.meta}
              </span>
              {it.badge && (
                <span className="pib-installed" data-testid={`${p}-chip-installed-${it.id}`}>
                  <Icon name="check" size={10} aria-hidden />
                  {it.badge}
                </span>
              )}
              <span aria-hidden className="pib-check">
                <Icon name="check" size={11} />
              </span>
            </button>
          ))
        )}

        {marquee && (
          <div
            aria-hidden
            data-testid={`${p}-marquee`}
            className="pib-marquee"
            style={{ position: 'absolute', ...marquee, pointerEvents: 'none' }}
          />
        )}
      </div>

      {/* 끄는 동안 커서에 붙는 그림 — 화면 밖에 두고 setDragImage 로만 쓴다. */}
      <div ref={ghostRef} aria-hidden className="pib-ghost" />
    </div>
  );
}

/**
 * 목록 찾기 칸 옆의 [+ 추가] 단추(부품 등록 · 변경점 추가). 찾기 칸과 높이를 맞추고(34px, Form 의
 * baseInput), 찾기 칸과 갈리도록 브랜드색 옅은 바탕으로 칠한다(2026-10-28 오너 "높이 다른거 불쾌 ·
 * 구분감 있는 색으로").
 */
export function AddButton({
  label,
  onClick,
  testId,
}: {
  label: string;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button type="button" className="pib-add-btn" onClick={onClick} data-testid={testId}>
      <Icon name="plus" size={13} strokeWidth={2.4} aria-hidden />
      {label}
    </button>
  );
}

// ── 오른쪽 열 머리 ──

export function SideColumn({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <div
      style={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      <div style={panelHeaderStyle}>
        {title}{' '}
        <span key={count} className="pib-count" style={{ color: 'var(--fg-muted)' }}>
          ({count})
        </span>
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {children}
      </div>
    </div>
  );
}

const BOARD_HEIGHT = 440;

export const panelStyle: CSSProperties = {
  height: BOARD_HEIGHT,
  minWidth: 0,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-sunken)',
  boxSizing: 'border-box',
};

export const boardGridStyle = (withList: boolean): CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: withList ? 'minmax(240px, 1fr) minmax(0, 2fr)' : 'minmax(0, 1fr)',
  gap: 12,
});

const stageStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  borderRadius: 'var(--r-card)',
};

const nameplateStyle: CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '3px 10px',
  fontSize: 12,
  fontWeight: 600,
  border: '1px solid var(--border-default)',
  borderRadius: 999,
  background: 'var(--bg-surface)',
};

const panelHeaderStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--fg-default)',
  marginBottom: 4,
};

export const emptyStyle: CSSProperties = {
  padding: 24,
  textAlign: 'center',
  fontSize: 12,
  color: 'var(--fg-muted)',
};

export const cardStyle: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  padding: 10,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

export const removeBtnStyle: CSSProperties = {
  padding: '3px 8px',
  fontSize: 11,
  border: '1px solid var(--fg-danger)',
  background: 'transparent',
  color: 'var(--fg-danger)',
  borderRadius: 'var(--r-btn)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
